/**
 * Post-login return URL helpers for session timeout / 401 handling in the legacy app.
 */

export function safeReturnPath(pathname, search = "") {
  const path = `${pathname || "/"}${search || ""}`
  if (!path.startsWith("/") || path.startsWith("//")) return null
  if (path.startsWith("/users/")) return null
  if (path.startsWith("/api/")) return null
  return path
}

export function readLoginBaseUrl() {
  if (typeof document === "undefined") return "/users/sign_in"
  return document.body?.dataset?.apexLegacyLoginUrl || "/users/sign_in"
}

export function loginUrlWithReturn(baseLoginUrl, returnPath) {
  let safe = null
  if (typeof returnPath === "string" && returnPath.startsWith("/") && !returnPath.startsWith("//")) {
    const qIdx = returnPath.indexOf("?")
    if (qIdx >= 0) {
      safe = safeReturnPath(returnPath.slice(0, qIdx), returnPath.slice(qIdx))
    } else {
      safe = safeReturnPath(returnPath, "")
    }
  }
  if (!safe) return baseLoginUrl

  try {
    const url = new URL(baseLoginUrl, window.location.origin)
    url.searchParams.set("next", safe)
    if (url.origin !== window.location.origin) {
      return url.href
    }
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    const sep = baseLoginUrl.includes("?") ? "&" : "?"
    return `${baseLoginUrl}${sep}next=${encodeURIComponent(safe)}`
  }
}

/** Redirect browser to login, preserving current or provided return path via ?next=. */
export function redirectToLoginWithReturnUrl(returnPath) {
  if (typeof window === "undefined") return false

  const path =
    (typeof returnPath === "string" && returnPath.startsWith("/") ? returnPath : null) ||
    safeReturnPath(window.location.pathname, window.location.search)

  const target = loginUrlWithReturn(readLoginBaseUrl(), path || undefined)
  window.location.assign(target)
  return true
}

/** When a same-origin fetch returns 401, send the user to login with return URL. */
export function handleUnauthorizedResponse(response) {
  if (response?.status !== 401) return false
  return redirectToLoginWithReturnUrl()
}

export function isSameOriginRequestUrl(url) {
  if (typeof window === "undefined" || !url) return false
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin
  } catch {
    return false
  }
}

/** Patch window.fetch once to redirect on same-origin 401 responses. */
export function installLegacyFetchAuthRedirect() {
  if (typeof window === "undefined" || window.__legacyFetchAuthRedirectInstalled) return
  window.__legacyFetchAuthRedirectInstalled = true

  const nativeFetch = window.fetch.bind(window)
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args)
    if (response.status === 401) {
      const input = args[0]
      const url = typeof input === "string" ? input : input?.url
      if (isSameOriginRequestUrl(url)) {
        handleUnauthorizedResponse(response)
      }
    }
    return response
  }
}
